"use client"

import {
  AutosaveIndicator,
  ErrorSummary,
  type ErrorSummaryItem,
  FormSection,
  FormShell,
  FormSkeleton,
  StickyActionBar,
} from "@/(admin)/components/form"
import Scheduled from "@/(admin)/modules/post/pages/Scheduled"
import AccessDenied from "@/components/custom-access-denied"
import { POSTS } from "@/constants/admin-paths"
import { postFetchPostByIdUseMine, useCheckPermission } from "@/hook/use-check-permission"
import { useFormAutosave } from "@/hook/useFormAutosave"
import { useFormShortcuts } from "@/hook/useFormShortcuts"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { getLocalizedHref } from "@/lib/config/lib/helper"
import { t } from "@/languages/i18n"
import { DEFAULT_DOMAIN, useDomainStore } from "@/stores/use-domain-store"
import { WebsiteResponseDataObject } from "@/types/codegen/data-contracts"
import {
  CategoryOption,
  FieldType,
  LiveNewsItemData,
  MediaData,
  PostData,
  PostWorkflowStatus,
  TagOption,
} from "@/types/types"
import { getDraftPreviewUrl, getPreviewUrl } from "@/utils/admin-preview-url"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { normalizePostStatus } from "@/utils/post-status-display"
import { purgeRemovedPostSlugs } from "@/validate/validatePosts"
import { EyeOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons"
import type { FormProps } from "antd"
import { Button, Form, Select } from "antd"
import debounce from "lodash/debounce"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import "react-quill/dist/quill.snow.css"
import { useSelector } from "react-redux"
import { RootState } from "../../auth/services/redux/store"
import { fetchCampaigns } from "../../campaign/services/campaignService"
import {
  addPost,
  approvePost,
  fetchPostId,
  fetchPostMineById,
  checkSlugAvailability,
  generateUniqueSlug,
  publishPost,
  rejectPost,
  requestReviewPost,
  schedulePost,
  schedulePostUnpublish,
  unPublishPost,
  updateMine,
  updatePost,
} from "../services/post"
import Categories from "./Categories"
import FeaturedImage from "./FeaturedImage"
import PostForm from "./PostForm"
import Publish from "./Publish"
import Websites from "./Websites"

const UpsertPost = () => {
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id
  const [quillValue, setQuillValue] = useState<string>("")
  const [post, setPost] = useState<PostData | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<Array<string | number>>([])
  const [selectedCategoryOptions, setSelectedCategoryOptions] = useState<CategoryOption[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)
  const [campaignOptions, setCampaignOptions] = useState<{ label: React.ReactNode; value: number }[]>([])
  const [selectedCampaignOption, setSelectedCampaignOption] = useState<{
    label: React.ReactNode
    value: number
  } | null>(null)
  const [loadingCampaignOptions, setLoadingCampaignOptions] = useState(false)
  const [selectedWebsites, setSelectedWebsites] = useState<number[]>([])
  const [status, setStatus] = useState<PostWorkflowStatus>("draft")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [unpublishAt, setUnpublishAt] = useState<string | null>(null)
  const [language, setLanguages] = useState("vi")
  const [selectedImage, setSelectedImage] = useState<MediaData | null>(null)
  const [selectedMetaImage, setSelectedMetaImage] = useState<MediaData | null>(null)
  const [loading, setLoading] = useState(isEditMode)
  const [approveLoading, setApproveLoading] = useState(false)
  const [rejectLoading, setRejectLoading] = useState(false)
  const [reviewActionLoading, setReviewActionLoading] = useState(false)
  const [liveNewsItems, setLiveNewsItems] = useState<LiveNewsItemData[]>([])
  const [isLiveNews, setIsLiveNews] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ErrorSummaryItem[]>([])
  const [reviewNote, setReviewNote] = useState<string | null>(null)
  const [submittedAt, setSubmittedAt] = useState<string | null>(null)
  const [reviewedAt, setReviewedAt] = useState<string | null>(null)
  const [form] = Form.useForm()
  const { hasPermission: checkPermission } = useCheckPermission()
  const router = useRouter()
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const isTamdaMedia = selectedDomain?.attributes?.domain === "tamdamedia.eu"
  const account = useSelector((state: RootState) => state.account)
  const originalDataRef = useRef({ oldtitle: "", oldslug: "", oldStatus: "draft" as PostWorkflowStatus })
  const isPreviewActionRef = useRef(false)
  const isViewActionRef = useRef(false)
  const isPublishActionRef = useRef(false)
  const [isFormReady, setIsFormReady] = useState(false)
  const [websiteError, setWebsiteError] = useState(false)
  const [imageError, setImageError] = useState(false)

  const canEditPost = checkPermission("post", "update") || checkPermission("post", "updateMine")
  // Chỉ user có quyền post.publish mới được "Xuất bản trực tiếp" khi tạo bài mới
  // (CTV không có quyền này → chỉ thấy nút "Lưu nháp").
  const canPublishDirectly = checkPermission("post", "publish")

  const fetchPostByIdUseMine = useMemo(() => postFetchPostByIdUseMine(checkPermission), [checkPermission])

  const fetchPostForEdit = useCallback(
    async (postId: number) => {
      return fetchPostByIdUseMine ? fetchPostMineById(postId) : fetchPostId(postId)
    },
    [fetchPostByIdUseMine],
  )

  const patchPost = useCallback(
    async (postId: number, data: any) => {
      if (checkPermission("post", "update")) {
        return updatePost(postId, data)
      }
      return updateMine(postId, data)
    },
    [checkPermission],
  )

  const toCampaignOption = (campaign: { id: number; attributes?: { name?: string; publishedAt?: string | null } }) => {
    const published = !!campaign.attributes?.publishedAt
    return {
      value: campaign.id,
      label: (
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${published ? "bg-green-500" : "bg-sky-500"}`} />
          <span>{campaign.attributes?.name || `#${campaign.id}`}</span>
        </span>
      ),
    }
  }

  const normalizePromoVideoUrls = (urls?: string[]) =>
    (urls || []).map(url => String(url || "").trim()).filter(url => !!url)

  const getNormalizedSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true)

    return {
      ...normalizeFormData(values),
      status,
      publishDate,
      language,
      featuredImage: selectedImage?.id || null,
      metaImage: selectedMetaImage?.id || null,
      categories: [...selectedCategories].sort(),
      tags: [...selectedTags].sort(),
      websites: [...selectedWebsites].sort(),
    }
  }, [
    form,
    status,
    publishDate,
    language,
    selectedCategories,
    selectedTags,
    selectedWebsites,
    selectedImage,
    selectedMetaImage,
  ])

  const { isChange, checkChange, commitSnapshot, resetSnapshot, handleCancel, message, modal } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })

  useEffect(() => {
    checkChange()
  }, [
    status,
    publishDate,
    language,
    selectedCategories,
    selectedTags,
    selectedWebsites,
    selectedImage,
    selectedMetaImage,
    checkChange,
  ])

  // Ảnh đại diện là dữ liệu cơ bản BẮT BUỘC (cả khi lưu nháp lẫn xuất bản).
  // Trả về false + đánh dấu lỗi/scroll tới khối ảnh nếu thiếu.
  const ensureFeaturedImage = useCallback(() => {
    if (selectedImage?.id) {
      setImageError(false)
      return true
    }
    setImageError(true)
    setValidationErrors([
      {
        fieldName: "featuredImage",
        message: t("Vui lòng chọn ảnh đại diện"),
      },
    ])
    document.getElementById("featuredImage")?.scrollIntoView({ behavior: "smooth", block: "center" })
    return false
  }, [selectedImage])

  // Website BẮT BUỘC cho mọi thao tác lưu (kể cả nháp): bài không gắn website
  // nào sẽ lọt khỏi bộ lọc domain → editor chỉ có quyền vài site không thấy lại
  // được bài của chính mình.
  const ensureWebsites = useCallback(() => {
    if (selectedWebsites && selectedWebsites.length > 0) {
      setWebsiteError(false)
      return true
    }
    setWebsiteError(true)
    setValidationErrors([
      {
        fieldName: "websites",
        message: t("Vui lòng chọn ít nhất một website áp dụng"),
      },
    ])
    return false
  }, [selectedWebsites])

  // Website + ảnh đại diện + nội dung là bắt buộc khi bài SẼ được xuất bản —
  // dùng chung cho cả submit qua Form (onFinish) LẪN các nút "Xuất bản"/"Lên
  // lịch"/đổi trạng thái ở khối review (những nút này gọi thẳng API publish,
  // không đi qua form.submit() nên trước đây không hề bị check).
  const ensurePublishRequirements = useCallback(
    (contentValue: unknown) => {
      if (!ensureWebsites()) {
        return false
      }

      if (!ensureFeaturedImage()) {
        return false
      }

      if (!(contentValue && String(contentValue).trim())) {
        setValidationErrors([{ fieldName: "content", message: t("Vui lòng thêm nội dung!") }])
        return false
      }

      return true
    },
    [ensureWebsites, ensureFeaturedImage],
  )

  const onFinish: FormProps<FieldType>["onFinish"] = async values => {
    let previewWindow = null

    if (isPreviewActionRef.current || isViewActionRef.current) {
      previewWindow = window.open("", "_blank")
    }

    // Lưu nháp (status vẫn là "draft" và không bấm "Xuất bản") chỉ cần title +
    // slug (rule required của Form.Item đã lo) + website — ảnh đại diện/nội dung
    // chỉ bắt buộc khi bài SẼ được xuất bản (bấm "Tạo & Xuất bản", hoặc status
    // đang là published/scheduled).
    const willPublish = isPublishActionRef.current || status === "published" || status === "scheduled"

    if (willPublish ? !ensurePublishRequirements(values.content) : !ensureWebsites()) {
      previewWindow?.close()
      return
    }

    setValidationErrors([])

    setWebsiteError(false)
    setImageError(false)

    try {
      setLoading(true)
      let finalSlug = values.slug

      // Slug trong form là nguồn sự thật (PostForm lo tự sinh / khoá). Edit mode KHÔNG
      // tự đổi slug theo tiêu đề mới nữa — trước đây đổi title là slug bị ghi đè dù
      // đang khoá, làm đổi URL bài đã có. Chỉ sinh dự phòng khi slug trống.
      if (values.title && !values.slug?.trim()) {
        // Cùng cách sinh với PostForm (handleTitleBlur): slug hợp lệ + không trùng.
        finalSlug = await generateUniqueSlug(values.title, isEditMode ? Number(id) : undefined)
      }

      const basePayload = {
        title: values.title,
        slug: finalSlug,
        content: values.content ?? "",
        description: values.description,
        categories: selectedCategories.map(id => Number(id)) || [],
        websites: selectedWebsites.map(id => Number(id)) || [],
        tags: selectedTags.map(id => Number(id)) || [],
        campaign: selectedCampaignId,
        promo_video_urls: normalizePromoVideoUrls(values.promo_video_urls),
        image: selectedImage?.id || null,
        cover: selectedImage?.id || null,
        isLiveNews: values.isLiveNews || false,
        isLiveHappening: values.isLiveHappening || false,
        liveNewsItem:
          values.liveNewsItem && values.liveNewsItem.length > 0
            ? values.liveNewsItem.map(item => ({
                title: item.title,
                date: item.date,
                detail: item.detail,
              }))
            : [],
        locale: language,
        status: status,
      }
      console.log(basePayload, "payload")
      if (isEditMode) {
        // Chụp slug cũ TRƯỚC khi ghi đè `originalDataRef` bên dưới — cần nó để
        // purge cứng URL cũ khi slug đổi (nếu không URL cũ vẫn serve stale).
        const previousSlug = originalDataRef.current.oldslug

        // Slug đổi → kiểm tra trùng TRƯỚC khi PUT (giống flow tạo bản dịch). Strapi
        // bắt unique slug trên mọi locale; trước đây PUT bị từ chối nhưng UI vẫn báo
        // thành công nên bản dịch "không được lưu" mà user không biết.
        if (finalSlug && finalSlug !== previousSlug) {
          const slugCheck = await checkSlugAvailability(finalSlug, Number(id))
          if (slugCheck?.isAvailable === false) {
            form.setFields([{ name: "slug", errors: [t("Đường dẫn đã tồn tại. Vui lòng chọn đường dẫn khác.")] }])
            form.scrollToField("slug", { behavior: "smooth", block: "center" })
            message.error(t("Đường dẫn đã tồn tại. Vui lòng chọn đường dẫn khác."))
            return
          }
        }

        const data = {
          data: {
            ...basePayload,
            readingTime: values.readingTime,
            shortDescription: values.shortDescription,
            publishedAt: post?.attributes?.publishedAt || null,
            scheduledAt: publishDate,
            seo: {
              metaTitle: values.metaTitle,
              metaDescription: values.metaDescription,
              keywords: values.keywords,
              metaRobots: values.metaRobots,
              metaViewport: values.metaViewport,
              structuredData: values.structuredData || null,
              metaImage: selectedMetaImage?.id || null,
              canonicalURL: values.canonicalURL,
            },
          },
        }

        // HttpClient nuốt lỗi HTTP (400 unique slug, 403, 500…) và trả null thay vì
        // throw → phải tự kiểm tra, nếu không sẽ báo "cập nhật thành công" dù không lưu.
        const updated = await patchPost(Number(id), data)
        if (!updated) {
          throw new Error("Update post failed")
        }

        setLiveNewsItems(values.liveNewsItem || [])
        setIsLiveNews(values.isLiveNews || false)
        originalDataRef.current = {
          oldtitle: values.title as string,
          oldslug: finalSlug as string,
          oldStatus: status,
        }
        commitSnapshot()
        message.success(t("Bạn đã cập nhật bài viết thành công"))
        // Đổi slug + bài đã publish → purge cứng URL slug CŨ (404 ngay) qua Server
        // Action. Slug mới để watermark SWR lo. Bài nháp chưa từng public nên bỏ qua.
        if (previousSlug && previousSlug !== finalSlug && post?.attributes?.publishedAt) {
          await purgeRemovedPostSlugs([previousSlug])
        }

        if (previewWindow) {
          const contentDomain =
            post?.attributes?.websites?.data?.find(website => selectedWebsites.includes(Number(website.id)))?.attributes
              ?.domain ?? selectedDomain?.attributes?.domain
          const previewPath = getLocalizedHref(finalSlug, language)
          if (isViewActionRef.current) {
            previewWindow.location.href = getPreviewUrl(contentDomain, previewPath)
          } else if (isPreviewActionRef.current) {
            // "Xem trước": mint token preview ngắn hạn rồi điều hướng sang
            // /api/draft/enable trên đúng public origin của site chứa bài viết.
            previewWindow.location.href = await getDraftPreviewUrl(contentDomain, previewPath)
          }
        }
      } else {
        const dataPayload = {
          ...basePayload,
          publishedAt: null,
          scheduledAt: status === "scheduled" ? publishDate : null,
          seo: {
            id: 0,
            metaTitle: values.metaTitle ? values.metaTitle : values.title,
            metaDescription: values.metaDescription ? values.metaDescription : values.title,
            canonicalURL: values.canonicalURL,
            keywords: values.keywords,
            metaRobots: values.metaRobots,
            metaViewport: values.metaViewport,
            structuredData: typeof values.structuredData === "string" ? JSON.parse(values.structuredData) : {},
          },
          author: account.user,
        }

        const response = await addPost({ data: dataPayload })
        const responseId = response?.data?.id
        // Cùng lý do như nhánh update: null = Strapi từ chối, không được báo thành công.
        if (!responseId) {
          throw new Error("Create post failed")
        }
        // Strapi.create luôn ép status=draft/publishedAt=null → muốn "Xuất bản
        // trực tiếp" phải gọi thêm /publish (chỉ mở cho quyền post.publish).
        const shouldPublish = isPublishActionRef.current && !!responseId
        if (shouldPublish) {
          await publishPost(Number(responseId))
        }
        commitSnapshot()
        message.success(shouldPublish ? t("Xuất bản bài viết thành công!") : t("Bài viết đã được thêm thành công!"))

        if (previewWindow) {
          const newEditUrl = window.location.pathname.replace("/create", "/edit") + `?id=${responseId}`
          window.history.replaceState(null, "", newEditUrl)
          const contentDomain = selectedDomain?.attributes?.domain
          const previewPath = getLocalizedHref(finalSlug, language)
          if (isViewActionRef.current) {
            previewWindow.location.href = getPreviewUrl(contentDomain, previewPath)
          } else {
            previewWindow.location.href = await getDraftPreviewUrl(contentDomain, previewPath)
          }
        } else if (responseId) {
          // Ở lại trang sau khi tạo (không redirect về danh sách): chuyển sang chế
          // độ chỉnh sửa của bài vừa tạo để admin bấm "Xem trước" được ngay. Trong
          // edit mode, "Xem trước" đi qua nhánh isEditMode của onFinish (đã hoạt động).
          router.replace(createPath(POSTS.EDIT.PATH, searchParams, { id: responseId }))
        } else {
          router.push(createPath(POSTS.LIST.PATH, searchParams))
        }
      }
    } catch (error) {
      message.error(isEditMode ? t("Lỗi khi cập nhật bài viết") : t("Thêm bài viết thất bại. Vui lòng thử lại."))
    } finally {
      setLoading(false)
      isPreviewActionRef.current = false
      isViewActionRef.current = false
      isPublishActionRef.current = false
    }
  }

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = errorInfo => {
    const items: ErrorSummaryItem[] = (errorInfo?.errorFields || []).map(field => ({
      fieldName: Array.isArray(field.name) ? field.name.join("_") : String(field.name),
      message: field.errors?.[0] || t("Lỗi không xác định"),
    }))
    setValidationErrors(items)
  }

  const refreshPostStatusState = async () => {
    if (!id) return
    const response = await fetchPostForEdit(Number(id))
    if (!response?.attributes) return
    const postData = response.attributes
    setPost(response)
    setStatus((postData.status as PostWorkflowStatus) || "draft")
    setPublishDate(postData.scheduledAt || null)
    setUnpublishAt(postData.unpublishAt || null)
    setReviewNote(postData.reviewNote ?? null)
    setSubmittedAt(postData.submittedAt ?? null)
    setReviewedAt(postData.reviewedAt ?? null)
  }

  const handleRequestReview = async () => {
    if (!id) {
      message.error(t("Không tìm thấy bài viết để yêu cầu kiểm duyệt."))
      return
    }
    try {
      setReviewActionLoading(true)
      await requestReviewPost(Number(id))
      await refreshPostStatusState()
      message.success(t("Đã gửi yêu cầu kiểm duyệt thành công."))
    } catch {
      message.error(t("Không thể gửi yêu cầu kiểm duyệt."))
    } finally {
      setReviewActionLoading(false)
    }
  }

  const handleApproveReview = async () => {
    if (!id) {
      message.error(t("Không tìm thấy bài viết để duyệt."))
      return
    }
    try {
      setApproveLoading(true)
      await approvePost(Number(id))
      await refreshPostStatusState()
      message.success(t("Duyệt bài viết thành công."))
    } catch {
      message.error(t("Có lỗi khi duyệt bài viết."))
    } finally {
      setApproveLoading(false)
    }
  }

  const handleUnpublish = async () => {
    if (!id) {
      message.error(t("Không tìm thấy bài viết để hủy xuất bản."))
      return
    }
    try {
      setReviewActionLoading(true)
      await unPublishPost(Number(id))
      await refreshPostStatusState()
      message.success(t("Hủy xuất bản bài viết thành công!"))
    } catch {
      message.error(t("Lỗi khi hủy xuất bản bài viết"))
    } finally {
      setReviewActionLoading(false)
    }
  }

  const handleRejectReview = async (reviewNote: string) => {
    if (!id) {
      message.error(t("Không tìm thấy bài viết để từ chối duyệt."))
      return
    }
    try {
      setRejectLoading(true)
      await rejectPost(Number(id), reviewNote || "Rejected by administrator")
      await refreshPostStatusState()
      message.success(t("Đã từ chối duyệt bài viết."))
    } catch {
      message.error(t("Có lỗi khi từ chối duyệt bài viết."))
    } finally {
      setRejectLoading(false)
    }
  }

  const handlePublishNow = async (nextUnpublishAt: string | null) => {
    if (!id) return
    if (!ensurePublishRequirements(form.getFieldValue("content"))) return
    try {
      setReviewActionLoading(true)
      await publishPost(Number(id), nextUnpublishAt)
      await refreshPostStatusState()
      message.success(t("Xuất bản bài viết thành công!"))
    } catch {
      message.error(t("Lỗi khi xuất bản bài viết"))
    } finally {
      setReviewActionLoading(false)
    }
  }

  const handleSchedulePublish = async (scheduledAt: string, nextUnpublishAt: string | null) => {
    if (!id) return
    if (!ensurePublishRequirements(form.getFieldValue("content"))) return
    try {
      setReviewActionLoading(true)
      await schedulePost(Number(id), scheduledAt, nextUnpublishAt)
      await refreshPostStatusState()
      message.success(t("Lên lịch bài viết thành công!"))
    } catch {
      message.error(t("Lỗi khi lên lịch bài viết"))
    } finally {
      setReviewActionLoading(false)
    }
  }

  const handleScheduleUnpublish = async (nextUnpublishAt: string | null) => {
    if (!id) return
    try {
      setReviewActionLoading(true)
      await schedulePostUnpublish(Number(id), nextUnpublishAt)
      await refreshPostStatusState()
      message.success(nextUnpublishAt ? t("Đã lưu lịch gỡ bài!") : t("Đã bỏ lịch gỡ bài"))
    } catch {
      message.error(t("Lỗi khi lưu lịch gỡ bài"))
    } finally {
      setReviewActionLoading(false)
    }
  }

  const handlePublishStatusChange = async (nextStatus: PostWorkflowStatus) => {
    // Thiếu website/ảnh đại diện/nội dung → không cho chuyển sang "published", giữ nguyên trạng thái.
    if (nextStatus === "published" && !ensurePublishRequirements(form.getFieldValue("content"))) return
    setStatus(nextStatus)
    if (!isEditMode || !id) return

    try {
      setReviewActionLoading(true)

      if (nextStatus === "published") {
        await publishPost(Number(id))
        message.success(t("Xuất bản bài viết thành công!"))
      } else if (nextStatus === "draft") {
        await patchPost(Number(id), { data: { publishedAt: null } })
        message.success(t("Chuyển thành bản nháp thành công!"))
      }
      await refreshPostStatusState()
    } catch {
      message.error(t("Lỗi khi cập nhật trạng thái bài viết"))
    } finally {
      setReviewActionLoading(false)
    }
  }

  useEffect(() => {
    if (isEditMode && id) {
      if (!canEditPost) {
        setLoading(false)
        return
      }
      const fetchPost = async () => {
        try {
          setLoading(true)
          const response = await fetchPostForEdit(Number(id))
          setPost(response)

          if (!response || !response.attributes) {
            message.error(t("Không tìm thấy bài viết hoặc dữ liệu không hợp lệ"))
            return
          }

          const postData = response.attributes
          const currentStatus = normalizePostStatus(postData.status, postData.publishedAt, postData.scheduledAt)

          setStatus(currentStatus)
          setPublishDate(postData.scheduledAt || null)
          setUnpublishAt(postData.unpublishAt || null)

          setReviewNote(postData.reviewNote ?? null)
          setSubmittedAt(postData.submittedAt ?? null)
          setReviewedAt(postData.reviewedAt ?? null)

          const hasLiveNewsItems = postData.liveNewsItem && postData.liveNewsItem.length > 0
          const shouldEnableLiveNews = postData.isLiveNews || hasLiveNewsItems

          const campaignData = postData?.campaign?.data

          const formData = {
            id: response.id,
            title: postData.title || "",
            description: postData.description || "",
            slug: postData.slug || "",
            content: postData.content || "",
            shortDescription: postData.shortDescription || "",
            readingTime: postData.readingTime || 0,
            isLiveNews: shouldEnableLiveNews,
            isLiveHappening: postData.isLiveHappening || false,
            liveNewsItem: postData.liveNewsItem || [],
            campaign: campaignData?.id,
            promo_video_urls: normalizePromoVideoUrls(postData?.promo_video_urls),
          }

          setLiveNewsItems(postData.liveNewsItem || [])
          setIsLiveNews(shouldEnableLiveNews)

          form.setFieldsValue(formData)
          form.setFieldValue("metaTitle", postData.name || postData.seo?.metaTitle || "")
          form.setFieldValue("metaDescription", postData.description || postData.seo?.metaDescription || "")
          form.setFieldValue("keywords", postData.seo?.keywords || "")
          form.setFieldValue("metaRobots", postData.seo?.metaRobots || "")
          form.setFieldValue("metaViewport", postData.seo?.metaViewport || "")
          form.setFieldValue("structuredData", JSON.stringify(postData.seo?.structuredData || {}))
          form.setFieldValue("canonicalURL", postData.slug || postData.seo?.canonicalURL || "")

          const categoryOptionsFromPost: CategoryOption[] =
            postData?.categories?.data?.map((category: any) => ({
              id: category?.id,
              value: category?.id,
              label: category?.attributes?.name || `#${category?.id}`,
              disabled: false,
              status: category?.attributes?.publishedAt ? "published" : "draft",
            })) ?? []
          setSelectedCategoryOptions(categoryOptionsFromPost)
          setSelectedCategories(categoryOptionsFromPost.map(category => category.id))
          if (postData?.websites?.data) {
            const websiteIds = postData.websites.data.map((site: WebsiteResponseDataObject) => site.id)
            setSelectedWebsites(websiteIds)
          } else {
            setSelectedWebsites([])
          }
          setSelectedTags(postData?.tags?.data?.map((tag: TagOption) => tag?.id) ?? [])
          if (campaignData?.id) {
            const option = toCampaignOption({
              id: campaignData.id,
              attributes: campaignData.attributes as { name?: string; publishedAt?: string | null } | undefined,
            })
            setCampaignOptions(current => {
              const existing = current.find(item => item.value === option.value)
              return existing ? [existing] : [option]
            })
            setSelectedCampaignId(campaignData.id)
            setSelectedCampaignOption(option)
          } else {
            setSelectedCampaignId(null)
            setSelectedCampaignOption(null)
            setCampaignOptions([])
          }
          setQuillValue(postData?.content)
          setLanguages(postData?.locale)
          const image = postData?.cover?.data || postData?.image?.data || null
          setSelectedImage(image)
          const metaImage = {
            id: postData?.seo?.metaImage?.data?.id ?? 0,
            ...postData?.seo?.metaImage?.data?.attributes,
          }
          setSelectedMetaImage(metaImage)
          originalDataRef.current = {
            oldtitle: postData.title || "",
            oldslug: postData.slug || "",
            oldStatus: currentStatus,
          }
        } catch (error) {
          console.error("Error fetching post:", error)
          message.error("Error fetching post")
        } finally {
          setLoading(false)
        }
      }

      fetchPost()
    } else {
      setIsFormReady(true)
    }
  }, [id, isEditMode, canEditPost, fetchPostByIdUseMine, fetchPostForEdit])

  // Khi tạo bài mới: mặc định chọn website = site admin đang làm việc (chỉ khi
  // đang ở phạm vi 1 site cụ thể, không phải "All websites"). Tránh admin phải
  // chọn lại hoặc quên chọn → báo lỗi. Chỉ set khi chưa có lựa chọn nào để không
  // đè thao tác của user, và chạy trước khi snapshot được commit nên không gây
  // trạng thái "chưa lưu" giả.
  useEffect(() => {
    if (isEditMode) return
    const siteId = selectedDomain?.id
    const isConcreteSite = !!siteId && siteId !== DEFAULT_DOMAIN.id && !!selectedDomain?.attributes?.domain
    if (isConcreteSite) {
      setSelectedWebsites(prev => (prev.length === 0 ? [siteId] : prev))
    }
  }, [isEditMode, selectedDomain])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot()
      })
      return () => cancelAnimationFrame(animationId)
    }
  }, [isFormReady, commitSnapshot])

  // When navigating between posts (same route, different ?id), the component
  // does NOT unmount. Reset the guard so the stale snapshot from the previous
  // post doesn't trigger a false dirty state on the new one.
  const prevIdRef = useRef(id)
  useEffect(() => {
    if (prevIdRef.current === id) return
    prevIdRef.current = id
    if (!isEditMode || !id) return
    resetSnapshot()
    setIsFormReady(false)
  }, [id, isEditMode, resetSnapshot])

  useEffect(() => {
    if (isEditMode) {
      const formContent = form.getFieldValue("content")
      if (formContent && formContent !== quillValue) {
        setQuillValue(formContent)
      }
    }
  }, [form, quillValue, isEditMode])

  const onChangeCategories = (checkedValues: Array<string | number>) => {
    setSelectedCategories(checkedValues)
  }

  const onChangeWebsites = (checkedValues: number[]) => {
    setSelectedWebsites(checkedValues)
    if (checkedValues.length > 0) setWebsiteError(false)
  }

  const onChangeTags = (checkedValues: string[]) => {
    setSelectedTags(checkedValues)
  }

  const onSearchCampaigns = useMemo(
    () =>
      debounce(async (keyword: string) => {
        const normalizedKeyword = keyword.trim()
        setLoadingCampaignOptions(true)

        try {
          const response = await fetchCampaigns({
            pageSize: "20",
            ...(normalizedKeyword ? { name: normalizedKeyword } : {}),
          })

          const options =
            response?.data?.map(
              (campaign: { id: number; attributes?: { name?: string; publishedAt?: string | null } }) =>
                toCampaignOption(campaign),
            ) || []

          if (!selectedCampaignOption) {
            setCampaignOptions(options)
            return
          }

          const existsInResult = options.some(
            (option: { label: React.ReactNode; value: number }) => option.value === selectedCampaignOption.value,
          )
          setCampaignOptions(existsInResult ? options : [selectedCampaignOption, ...options])
        } finally {
          setLoadingCampaignOptions(false)
        }
      }, 400),
    [selectedCampaignOption],
  )

  useEffect(() => {
    return () => {
      onSearchCampaigns.cancel()
    }
  }, [onSearchCampaigns])

  const handleSelectMedia = (media: MediaData | null) => {
    setSelectedImage(media)
    if (media?.id) setImageError(false)
  }

  const onChangeMetaImage = (media: MediaData | null) => {
    setSelectedMetaImage(media)
  }

  const handlePreviewRequest = () => {
    isPreviewActionRef.current = true
    isViewActionRef.current = false
    isPublishActionRef.current = false
    form.submit()
  }

  const handleViewRequest = () => {
    isViewActionRef.current = true
    isPreviewActionRef.current = false
    isPublishActionRef.current = false
    form.submit()
  }

  const handleNormalSubmit = () => {
    isPreviewActionRef.current = false
    isViewActionRef.current = false
    isPublishActionRef.current = false
    form.submit()
  }

  // "Xuất bản trực tiếp" khi tạo bài mới: lưu bản nháp rồi publish luôn.
  const handleCreateAndPublish = () => {
    isPreviewActionRef.current = false
    isViewActionRef.current = false
    isPublishActionRef.current = true
    form.submit()
  }

  const localizations = useMemo(() => {
    const data = post?.attributes?.localizations?.data
    if (!Array.isArray(data)) return []
    return data
      .filter((loc: any) => loc?.id && loc?.attributes?.locale)
      .map((loc: any) => ({ id: loc.id as number, locale: loc.attributes.locale as string }))
  }, [post])

  const isDraftMode = status === "draft"
  const autosaveValuesRef = useRef<unknown>(null)
  autosaveValuesRef.current = getNormalizedSnapshot()

  const performDraftSave = useCallback(async () => {
    if (!isEditMode) return
    const values = form.getFieldsValue(true)
    const draftPayload = {
      data: {
        title: values.title,
        slug: values.slug,
        content: values.content ?? "",
        description: values.description,
        publishedAt: null,
        scheduledAt: publishDate,
      },
    }
    await patchPost(Number(id), draftPayload as any)
    commitSnapshot()
  }, [isEditMode, id, form, quillValue, publishDate, commitSnapshot, patchPost])

  const autosave = useFormAutosave({
    enabled: isDraftMode && isEditMode && isFormReady,
    isDirty: isChange,
    onSave: performDraftSave,
    debounceMs: 3000,
    minIntervalMs: 10_000,
  })

  useFormShortcuts({
    enabled: isFormReady,
    onSaveDraft: () => {
      if (isEditMode && isDraftMode) {
        autosave.saveNow()
      } else {
        handleNormalSubmit()
      }
    },
    onPrimary: handleNormalSubmit,
    onCancel: () => handleCancel(createPath(POSTS.LIST.PATH, searchParams)),
  })

  if (isEditMode ? !canEditPost : !checkPermission("post", "create")) {
    return <AccessDenied />
  }

  if (loading && isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={3} sidebarCards={6} />
  }

  const crumbs = [
    { title: t("Bài viết"), href: createPath(POSTS.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const errorSummary =
    validationErrors.length > 0 ? (
      <ErrorSummary
        errors={validationErrors}
        onDismiss={() => setValidationErrors([])}
        resolveFieldId={fieldName =>
          fieldName === "featuredImage" || fieldName === "websites" ? fieldName : `post_${fieldName}`
        }
      />
    ) : null

  const main = (
    <PostForm
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValidationErrorsChange={setValidationErrors}
      quillValue={quillValue}
      setQuillValue={setQuillValue}
      onChangeMetaImage={onChangeMetaImage}
      initialLiveNewsItems={liveNewsItems}
      initialIsLiveNews={isLiveNews}
      onChangeContent={checkChange}
      onValuesChange={checkChange}
      onEditorReadyCb={() => {
        setIsFormReady(true)
      }}
      initialSlug={isEditMode ? (post?.attributes?.slug ?? "") : undefined}
    />
  )

  const sidebar = (
    <>
      <FormSection title={t("Xuất bản")} collapsible={false} compact>
        <Publish
          onSubmit={handleNormalSubmit}
          onPreview={handlePreviewRequest}
          onView={handleViewRequest}
          isCreateEdit={!isEditMode}
          isEditMode={isEditMode}
          localizations={localizations}
          onNavigateToLocale={locId => {
            const go = () => router.push(`/admin/posts/edit?id=${locId}`)
            if (!isChange) {
              go()
              return
            }
            modal.confirm({
              title: t("Cảnh báo: Dữ liệu chưa được lưu!"),
              content: t("Bạn có những thay đổi chưa lưu. Nếu thoát bây giờ, dữ liệu của bạn sẽ bị mất."),
              okText: t("Thoát và Bỏ qua"),
              cancelText: t("Ở lại trang"),
              okButtonProps: { danger: true },
              onOk: go,
            })
          }}
          onCreateLocale={targetLocale => {
            const go = () => router.push(`/admin/posts/translate?id=${id}&locale=${targetLocale}`)
            if (!isChange) {
              go()
              return
            }
            modal.confirm({
              title: t("Cảnh báo: Dữ liệu chưa được lưu!"),
              content: t("Bạn có những thay đổi chưa lưu. Nếu thoát bây giờ, dữ liệu của bạn sẽ bị mất."),
              okText: t("Thoát và Bỏ qua"),
              cancelText: t("Ở lại trang"),
              okButtonProps: { danger: true },
              onOk: go,
            })
          }}
          viOnly={isTamdaMedia}
          onStatusChange={nextStatus => handlePublishStatusChange(nextStatus as PostWorkflowStatus)}
          language={language}
          setLanguages={setLanguages}
          status={status}
          approveLoading={approveLoading}
          rejectLoading={rejectLoading}
          reviewActionLoading={reviewActionLoading}
          onRequestReview={handleRequestReview}
          onApprove={handleApproveReview}
          onReject={handleRejectReview}
          onUnpublish={handleUnpublish}
          onPublishNow={handlePublishNow}
          onSchedulePublish={handleSchedulePublish}
          unpublishAt={unpublishAt}
          onScheduleUnpublish={handleScheduleUnpublish}
          publishDate={publishDate}
          setPublishDate={setPublishDate}
          reviewNote={reviewNote}
          submittedAt={submittedAt}
          reviewedAt={reviewedAt}
        />
        <Scheduled status={status} publishDate={publishDate} setPublishDate={setPublishDate} isLoading={loading} />
      </FormSection>

      <FormSection title={t("Danh mục")} compact>
        <Categories
          selectedCategories={selectedCategories}
          onChangeCategories={onChangeCategories}
          selectedCategoryOptions={selectedCategoryOptions}
          locale={language}
        />
      </FormSection>

      {!isTamdaMedia && (
        <FormSection title={t("Chiến dịch")} compact>
          <Select
            className="w-full"
            showSearch
            allowClear
            value={selectedCampaignId ?? undefined}
            placeholder={t("Tìm chiến dịch")}
            options={campaignOptions}
            loading={loadingCampaignOptions}
            filterOption={false}
            onSearch={onSearchCampaigns}
            onFocus={() => onSearchCampaigns("")}
            onChange={(value: number | undefined, option: any) => {
              if (!value) {
                setSelectedCampaignId(null)
                setSelectedCampaignOption(null)
                return
              }
              setSelectedCampaignId(value)
              const normalizedOption = Array.isArray(option) ? option[0] : option
              setSelectedCampaignOption(normalizedOption || campaignOptions.find(item => item.value === value) || null)
            }}
          />
        </FormSection>
      )}

      <div
        id="websites"
        className={
          websiteError
            ? "rounded-lg ring-2 ring-red-500 ring-offset-2 transition-shadow"
            : "rounded-lg ring-0 transition-shadow"
        }
      >
        <FormSection title={t("Website áp dụng")} compact>
          <Websites selectedWebsites={selectedWebsites} onChangeWebsites={onChangeWebsites} />
        </FormSection>
      </div>

      <div
        id="featuredImage"
        className={
          imageError
            ? "rounded-lg ring-2 ring-red-500 ring-offset-2 transition-shadow"
            : "rounded-lg ring-0 transition-shadow"
        }
      >
        <FormSection title={t("Ảnh đại diện")} compact>
          <FeaturedImage selectedMedia={selectedImage} setSelectedMedia={handleSelectMedia} />
        </FormSection>
      </div>
    </>
  )

  const actionBar = (
    <StickyActionBar
      left={<AutosaveIndicator state={autosave.state} lastSavedAt={autosave.lastSavedAt} onRetry={autosave.saveNow} />}
      right={
        <>
          {/* "Xem trước" chỉ có nghĩa khi bài đã tồn tại. Ở chế độ tạo mới, bấm
              preview sẽ phải tạo bài trước → gây khó hiểu, nên ẩn đi. */}
          {isEditMode && (
            <Button icon={<EyeOutlined />} onClick={handlePreviewRequest} disabled={loading}>
              {t("Xem trước")}
            </Button>
          )}
          {isEditMode && isDraftMode && (
            <Button
              icon={<SaveOutlined />}
              onClick={() => autosave.saveNow()}
              loading={autosave.state === "saving"}
              disabled={loading}
            >
              {t("Lưu nháp")}
            </Button>
          )}
          {isEditMode ? (
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleNormalSubmit}
              loading={loading && !reviewActionLoading}
            >
              {t("Cập nhật")}
            </Button>
          ) : (
            <>
              {/* Nút "Xuất bản" cũ thực chất chỉ tạo bản nháp → đổi tên cho đúng. */}
              <Button
                type={canPublishDirectly ? "default" : "primary"}
                icon={<SaveOutlined />}
                onClick={handleNormalSubmit}
                loading={loading && !reviewActionLoading}
              >
                {t("Tạo bản nháp")}
              </Button>
              {canPublishDirectly && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleCreateAndPublish}
                  loading={loading && !reviewActionLoading}
                >
                  {t("Tạo & Xuất bản")}
                </Button>
              )}
            </>
          )}
        </>
      }
    />
  )

  return (
    <FormShell
      title={isEditMode ? t("Chỉnh sửa bài viết") : t("Tạo bài viết mới")}
      subtitle={isEditMode && post?.attributes?.title ? `${post.attributes.title}` : undefined}
      crumbs={crumbs}
      errorSummary={errorSummary}
      main={main}
      sidebar={sidebar}
      actionBar={actionBar}
    />
  )
}

export default UpsertPost
